import useUpdateRecordDialog from 'hooks/useUpdateRecordDialog';
import { useUpdateTeamMutation } from 'store/slices/apiV1/properties';
import {
  createUpdateTeamFormConstants,
  createUpdateTeamValidationSchema,
} from 'views/properties/Teams/components/constants';
import {
  HookFormInput,
  HookFormSelect,
  HookFormCheckbox,
} from 'ui-component/HookFormComponents';
import { Team } from 'types/properties';
import { HookFormTeamTypeAutocomplete } from 'ui-component/HookFormComponents/HookFormTeamTypeAutocomplete';
import HookFormStockLocationSelect from 'ui-component/HookFormComponents/HookFormStockLocationSelect';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import {
  getShipmentCarrierAndService,
  getShipmentMethod,
} from 'utils/functions';
import { ShippingMethods } from 'types/purchasing';
import { ListSubheader, MenuItem, Stack } from '@mui/material';
import { cofactrSlug } from 'constants/appConstants';
import { StockLocationLite, StockLocationType } from 'types/inventory';
import _ from 'lodash';
import FormSection from 'ui-component/FormSection';

const toStockLocationId = (
  v: StockLocationLite | string | null | undefined
): string => (v == null ? '' : typeof v === 'object' ? v.id : v);

type UpdateTeamDialogProps = {
  dialogOpen: boolean;
  onClose: () => void;
  teamToUpdate: Team;
};

const UpdateTeamDialog = ({
  dialogOpen,
  onClose,
  teamToUpdate,
}: UpdateTeamDialogProps) => {
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const formDefaults = {
    [createUpdateTeamFormConstants.name.id]: teamToUpdate.name ?? '',
    [createUpdateTeamFormConstants.teamType.id]:
      teamToUpdate?.teamType?.id ?? '',
    [createUpdateTeamFormConstants.industry.id]: teamToUpdate.industry ?? '',
    [createUpdateTeamFormConstants.militaryUse.id]:
      teamToUpdate.militaryUse ?? false,
    [createUpdateTeamFormConstants.dpas.id]: teamToUpdate.dpas ?? '',
    [createUpdateTeamFormConstants.defaultShippingMethod.id]: getShipmentMethod(
      {
        carrier: teamToUpdate.defaultShippingCarrier,
        service: teamToUpdate.defaultShippingService,
      }
    ),
    [createUpdateTeamFormConstants.defaultCofactrWarehouse.id]:
      toStockLocationId(teamToUpdate.defaultCofactrWarehouse),
    [createUpdateTeamFormConstants.defaultShippingDestinationLocation.id]:
      toStockLocationId(teamToUpdate.defaultShippingDestinationLocation),
  };

  const preSubmit = (data: Record<string, unknown>) => {
    const payload = { ...data };
    const method = _.get(
      payload,
      createUpdateTeamFormConstants.defaultShippingMethod.id
    ) as string | undefined;
    if (method) {
      const { defaultShippingCarrier, defaultShippingService } =
        getShipmentCarrierAndService(method);
      _.set(payload, 'defaultShippingCarrier', defaultShippingCarrier);
      _.set(payload, 'defaultShippingService', defaultShippingService);
    }
    _.unset(payload, createUpdateTeamFormConstants.defaultShippingMethod.id);
    const warehouse = _.get(
      payload,
      createUpdateTeamFormConstants.defaultCofactrWarehouse.id
    );
    const destination = _.get(
      payload,
      createUpdateTeamFormConstants.defaultShippingDestinationLocation.id
    );
    _.set(
      payload,
      createUpdateTeamFormConstants.defaultCofactrWarehouse.id,
      warehouse === '' ? null : warehouse
    );
    _.set(
      payload,
      createUpdateTeamFormConstants.defaultShippingDestinationLocation.id,
      destination === '' ? null : destination
    );
    return payload;
  };

  const { methods, control, errors, BaseUpdateRecordDialog, dialogProps } =
    useUpdateRecordDialog({
      formConstants: createUpdateTeamFormConstants,
      validationSchema: createUpdateTeamValidationSchema(
        handleReplaceWithCustomTeamName
      ),
      useUpdateMutation: useUpdateTeamMutation,
      newRecordDefaults: formDefaults,
      preSubmit: preSubmit as (data: object) => object,
      recordName: handleReplaceWithCustomTeamName('Team'),
      idProp: 'id',
      selectedRecord: teamToUpdate,
    });

  return (
    <BaseUpdateRecordDialog
      open={dialogOpen}
      onClose={onClose}
      dialogProps={dialogProps}
    >
      <Stack spacing={2}>
        <HookFormInput
          name={createUpdateTeamFormConstants.name.id}
          label={handleReplaceWithCustomTeamName(
            createUpdateTeamFormConstants.name.label
          )}
          control={control}
          errors={errors}
        />
        <HookFormTeamTypeAutocomplete
          name={createUpdateTeamFormConstants.teamType.id}
          label={handleReplaceWithCustomTeamName(
            createUpdateTeamFormConstants.teamType.label
          )}
          control={control}
          errors={errors}
          defaultValue={
            formDefaults[createUpdateTeamFormConstants.teamType.id] as
              | string
              | undefined
          }
          setValue={methods.setValue}
        />
        <FormSection title="Optional Settings to Override Org Defaults">
          <Stack spacing={2}>
            <HookFormInput
              name={createUpdateTeamFormConstants.industry.id}
              label={createUpdateTeamFormConstants.industry.label}
              control={control}
              errors={errors}
            />
            <HookFormCheckbox
              name={createUpdateTeamFormConstants.militaryUse.id}
              label={createUpdateTeamFormConstants.militaryUse.label}
              control={control}
              errors={errors}
            />
            <HookFormInput
              name={createUpdateTeamFormConstants.dpas.id}
              label={createUpdateTeamFormConstants.dpas.label}
              control={control}
              errors={errors}
            />
            <HookFormSelect
              name={createUpdateTeamFormConstants.defaultShippingMethod.id}
              label={createUpdateTeamFormConstants.defaultShippingMethod.label}
              control={control}
              errors={errors}
            >
              {ShippingMethods.map((method) =>
                method.type === 'option' ? (
                  <MenuItem value={method.value} key={method.value}>
                    {method.label}
                  </MenuItem>
                ) : (
                  <ListSubheader key={method.value}>
                    {method.label}
                  </ListSubheader>
                )
              )}
            </HookFormSelect>
            <HookFormStockLocationSelect
              name={createUpdateTeamFormConstants.defaultCofactrWarehouse.id}
              label={
                createUpdateTeamFormConstants.defaultCofactrWarehouse.label
              }
              control={control}
              errors={errors}
              filters={[
                {
                  field: 'locationType',
                  operator: '=',
                  value: StockLocationType.FACILITY,
                },
                {
                  // @ts-ignore because query filter typing does not support nested filters like this
                  field: 'org.slug',
                  operator: '=',
                  value: cofactrSlug,
                },
                {
                  field: 'warehouseShippingEnabled',
                  operator: '=',
                  value: true,
                },
              ]}
              mb={0}
            />
            <HookFormStockLocationSelect
              name={
                createUpdateTeamFormConstants.defaultShippingDestinationLocation
                  .id
              }
              label={
                createUpdateTeamFormConstants.defaultShippingDestinationLocation
                  .label
              }
              control={control}
              errors={errors}
              setValue={methods.setValue}
              contextualCreate
              limitCreateLocationTypeToFacility
              filters={[
                {
                  field: 'locationType',
                  operator: '=',
                  value: StockLocationType.FACILITY,
                },
              ]}
              mb={0}
            />
          </Stack>
        </FormSection>
      </Stack>
    </BaseUpdateRecordDialog>
  );
};

export default UpdateTeamDialog;
